import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs/internal/Observable';
import { EMPTY } from 'rxjs/internal/observable/empty';
import { filter } from 'rxjs/internal/operators/filter';
import { switchMap } from 'rxjs/internal/operators/switchMap';
import { take } from 'rxjs/internal/operators/take';
import {
  AddServiceProviderWebRequest,
  MhsConfigurationApiClient,
  ServiceProviderDto,
  ServiceProviderHeaderRowItemSearchResult,
  UpdateServiceProviderWebRequest,
} from '../../../core/api/mhsConfigurationApiClient';
import { AuthStore } from '../../../core/authentication/authStore';
import { UsertStore } from '../../../core/context/userStore';

@Injectable({
  providedIn: 'root',
})
export class ServiceProvidersService {
  configurationApiClient = inject(MhsConfigurationApiClient);
  authStore = inject(AuthStore);
  userStore = inject(UsertStore);

  hide(serviceProviderId: number): Observable<void> {
    if (this.authStore.accessToken() === '') {
      return EMPTY;
    }
    return this.userStore.currentTenant$.pipe(
      filter((tenant) => tenant !== null),
      take(1),
      switchMap((tenant) => {
        const tenantName = tenant?.tenantName;
        return this.configurationApiClient.serviceProvider_Hide(serviceProviderId);
      }),
    );
  }
  reveal(serviceProviderId: number): Observable<void> {
    if (this.authStore.accessToken() === '') {
      return EMPTY;
    }
    return this.userStore.currentTenant$.pipe(
      filter((tenant) => tenant !== null),
      take(1),
      switchMap((tenant) => {
        const tenantName = tenant?.tenantName;
        return this.configurationApiClient.serviceProvider_Unhide(serviceProviderId);
      }),
    );
  }
  update(serviceProvider: UpdateServiceProviderWebRequest): Observable<ServiceProviderDto> {
    if (this.authStore.accessToken() === '') {
      return EMPTY;
    }
    return this.userStore.currentTenant$.pipe(
      filter((tenant) => tenant !== null),
      take(1),
      switchMap((tenant) => {
        const tenantName = tenant?.tenantName;
        return this.configurationApiClient.serviceProvider_Update(serviceProvider);
      }),
    );
  }
  create(serviceProvider: AddServiceProviderWebRequest): Observable<ServiceProviderDto> {
    if (this.authStore.accessToken() === '') {
      return EMPTY;
    }
    return this.userStore.currentTenant$.pipe(
      filter((tenant) => tenant !== null),
      take(1),
      switchMap((tenant) => {
        const tenantName = tenant?.tenantName;
        return this.configurationApiClient.serviceProvider_Add(serviceProvider);
      }),
    );
  }

  get(serviceProviderId: number): Observable<ServiceProviderDto> {
    if (this.authStore.accessToken() === '') {
      return EMPTY;
    }
    return this.userStore.currentTenant$.pipe(
      filter((tenant) => tenant !== null),
      take(1),
      switchMap((tenant) => {
        const tenantName = tenant?.tenantName;
        return this.configurationApiClient.serviceProvider_Get(serviceProviderId);
      }),
    );
  }

  search(
    pageIndex: number,
    pageSize: number,
    phrase: string,
    type?: string,
    orderBy?: string,
    isOrderByAsc?: boolean,
  ): Observable<ServiceProviderHeaderRowItemSearchResult> {
    if (this.authStore.accessToken() === '') {
      return EMPTY;
    }
    return this.userStore.currentTenant$.pipe(
      filter((tenant) => tenant !== null),
      take(1),
      switchMap((tenant) => {
        const tenantName = tenant?.tenantName;
        return this.configurationApiClient.serviceProvider_Search(
          type,
          phrase,
          pageIndex,
          pageSize,
          orderBy,
          isOrderByAsc,
        );
      }),
    );
  }
}
